import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { mapAuthError } from "@/lib/auth/errors";
import { classifyRecoveryLink, hasRecoveryError, type RecoveryLinkStatus } from "@/lib/auth/recovery-link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Passwort zurücksetzen – Xenomons" },
      { name: "description", content: "Setze dein Xenomons-Passwort zurück." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ResetPasswordPage,
});

type LinkStatus = RecoveryLinkStatus | "checking";

/**
 * Backstop for the link check. `getSession()` awaits Supabase's own URL
 * evaluation, so it normally settles within milliseconds; this timeout only
 * guards against the page hanging in "checking" if that promise never resolves.
 */
const RECOVERY_GRACE_MS = 8_000;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<LinkStatus>("checking");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    const search = window.location.search;

    // A URL that already carries an error is decided; nothing to wait for.
    if (hasRecoveryError(hash, search)) {
      setStatus("invalid");
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (next: Exclude<LinkStatus, "checking">) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      setStatus(next);
    };

    timer = setTimeout(() => settle("invalid"), RECOVERY_GRACE_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) settle("ready");
    });

    // `getSession()` awaits Supabase's URL evaluation before resolving. If no
    // session exists afterwards, the recovery link failed: on a failed URL
    // login Supabase deliberately creates no session and emits no event, so
    // waiting for PASSWORD_RECOVERY alone would leave the form dead forever.
    void supabase.auth.getSession().then(({ data }) => {
      settle(classifyRecoveryLink({ hash, search, hasSession: Boolean(data.session) }));
    });

    return () => {
      if (timer !== undefined) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth:reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("auth:reset.mismatch"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error(mapAuthError(error.message, t));
      return;
    }
    toast.success(t("auth:reset.success"));
    navigate({ to: "/game", replace: true });
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher variant="outline" showLabel />
      </div>
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-primary/30 bg-card/80 p-8 shadow-glow backdrop-blur-xl">
        <div className="mb-6 text-center">
          <h1 className="font-display text-3xl font-black text-glow">XENOMONS</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("auth:reset.title")}</p>
        </div>

        {status === "checking" ? (
          <p className="py-6 text-center text-sm text-muted-foreground" aria-live="polite">
            {t("auth:reset.checking")}
          </p>
        ) : status === "invalid" ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">{t("auth:reset.invalidLink")}</p>
            <Button asChild variant="secondary" className="w-full">
              <Link to="/auth">{t("auth:reset.backToLogin")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("auth:reset.description")}</p>
            <div>
              <Label htmlFor="new-pw">{t("auth:reset.newPassword")}</Label>
              <Input
                id="new-pw"
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
              <p className="mt-1 text-xs text-muted-foreground">{t("auth:errors.passwordRequirements")}</p>
            </div>
            <div>
              <Label htmlFor="confirm-pw">{t("auth:reset.confirmPassword")}</Label>
              <Input
                id="confirm-pw"
                type="password"
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-glow"
            >
              {loading ? t("auth:buttons.loading") : t("auth:reset.submit")}
            </Button>
            <Button asChild variant="secondary" className="w-full">
              <Link to="/auth">{t("auth:reset.backToLogin")}</Link>
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
